import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

function formatCurrency(num) {
  if (num == null || Number.isNaN(Number(num))) return "—";
  return `SAR ${Number(num).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function SummaryRow({ label, value, valueColor = "text.primary", size = 14, bold = false }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography sx={{ fontSize: size, color: "text.muted" }}>{label}</Typography>
      <Typography sx={{ fontSize: size + 2, fontWeight: bold ? 700 : 600, color: valueColor }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function StakeConfirmModal({ open, onClose, pool, amount, calc, categoryIcon, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!pool || !calc) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e?.data?.message || e?.error || "Could not process your stake. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography fontWeight={700} fontSize={22} sx={{ color: "text.primary" }}>
          Stake Your Tokens
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Review the details below and confirm your stake
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={submitting}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "border.subtle" }}>
        <Stack spacing={2.5}>
          {/* Pool info */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
              bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {categoryIcon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
                  {pool.poolName}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "text.muted", mt: 0.25 }}>
                  {pool.category ? `${pool.category} Staking` : pool.assetName}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2, borderColor: (theme) => theme.palette.accent.teal.border }} />

            <Grid container spacing={2}>
              <Grid size={4}>
                <Typography sx={{ fontSize: 12, color: "text.muted" }}>APY</Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: (theme) => theme.palette.accent.teal.chipText,
                    mt: 0.25,
                  }}
                >
                  {pool.annualYield}%
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography sx={{ fontSize: 12, color: "text.muted" }}>Lock Period</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.primary", mt: 0.25 }}>
                  {pool.lockPeriod} days
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography sx={{ fontSize: 12, color: "text.muted" }}>Min Stake</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.primary", mt: 0.25 }}>
                  {formatCurrency(pool.minStake)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Staking summary */}
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              bgcolor: "background.subtle",
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary", mb: 2 }}>
              Staking Summary
            </Typography>
            <Stack spacing={1.5}>
              <SummaryRow label="Amount to Stake" value={formatCurrency(amount)} bold size={15} />
              <SummaryRow label="Lock Period" value={`${calc.lockPeriod} days`} />
              <SummaryRow
                label="Estimated Rewards"
                value={formatCurrency(calc.reward)}
                valueColor={(theme) => theme.palette.chips.success.text}
              />
              <Divider />
              <SummaryRow
                label="Total at Maturity"
                value={formatCurrency(calc.total)}
                valueColor={(theme) => theme.palette.accent.teal.chipText}
                bold
                size={16}
              />
            </Stack>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            py: 1.4,
            fontWeight: 700,
            fontSize: 16,
            color: "common.white",
            background: (theme) => theme.palette.gradients.teal,
            "&:hover": {
              background: (theme) => theme.palette.gradients.tealHover,
            },
          }}
        >
          {submitting ? "Processing…" : "Confirm Stake"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
